import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NodeActivityEditor from '../../NodeActivityEditor';
import IconButton from '../../IconButton';
import PrimaryButton from '../../PrimaryButton';
import ProgressBar from '../../ProgressBar';
import LegacyMedia from '../../LegacyMedia';
import CheckIcon from '../../../../public/assets/images/theme-2/icons/handin-hover.svg';
import { formatBody } from '../../../utils';
import { ViewSection, LockInfo } from '../styles';
import {
  Questions,
  Question,
  SubmitPanel,
  Alternatives,
  AlternativeWrapper,
  Alternative,
  AlternativeOption,
  InfoPanel,
  Info,
  ProgressPanel,
  QuizInfo,
} from '../../NodeContentPreview/styles';
import ImmersiveReaderButton from '../../ImmersiveReaderButton';

const QuizContentViewer = ({ type, report, saveReport, data, isTeacher }) => {
  const [markedAnswers, setMarkedAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [corrected, setCorrected] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [redoable, setRedoable] = useState(false);
  const [locked, setLocked] = useState(false);
  const [doSave, setDoSave] = useState(false);

  const isEditable = !isTeacher && !locked && (redoable || !corrected);
  const questionCount = questions?.length || 0;

  useEffect(() => {
    const newQuestions = data.QuizQuestions || [];
    setQuestions(newQuestions);
    setMarkedAnswers(new Array(newQuestions.length));
    setRedoable(data.redoable);
    setLocked(report?.locked);
  }, []);

  useEffect(() => {
    if (questionCount) {
      const newAnswers = [];
      const oldAnswers = report?.QuizAnswers || data?.QuizAnswers || [];
      if (oldAnswers.length) {
        questions.forEach((question) => {
          const answer = oldAnswers.find((answer) => answer.question_id === question.id);
          if (answer) {
            newAnswers.push(answer.alternative_id);
          }
        });
        setMarkedAnswers(newAnswers);
        if (oldAnswers.length === questions.length) {
          correctQuiz(newAnswers);
        }
      }
    }
  }, [questions]);

  useEffect(() => {
    const count = markedAnswers.filter((answer) => !!answer).length;
    setProgress(count);
  }, [markedAnswers]);

  useEffect(() => {
    if (corrected && progress && doSave) {
      const answers = {};
      questions.forEach((question, index) => {
        answers[`_${question.id}`] = markedAnswers[index];
      });
      saveReport({ ...report, state: 'accept', type, answers });
    }
  }, [corrected, progress, questions, doSave]);

  const scrollToQuestion = (index) => {
    const question = document.getElementById(`question_${index}`);
    const contentPanel = document.getElementsByClassName(`content-panel`)[0];
    if (question && contentPanel) {
      contentPanel.scrollTo(0, question.offsetTop - 80);
    }
  };

  const getViewForAlternative = (alternative, questionIndex, isQuestionCorrect) => {
    const { id, body, correct } = alternative;

    const markAnswer = (event) => {
      if (isEditable || (redoable && !corrected)) {
        event.target.blur();
        const newMarkedAnswers = [...markedAnswers];
        newMarkedAnswers[questionIndex] = id;
        setMarkedAnswers(newMarkedAnswers);
        scrollToQuestion(questionIndex + 1);
      }
    };

    let className = '';
    if (corrected && !isQuestionCorrect) {
      if (correct) {
        className = 'correct';
      } else if (markedAnswers[questionIndex] === id) {
        className = 'error';
      }
    }

    const alternativeBody = (
      <Alternative className="content-section" dangerouslySetInnerHTML={{ __html: formatBody(body) }} />
    );

    return (
      <>
        {alternative.media_preview ? (
          <div>
            {alternativeBody}
            <LegacyMedia item={alternative} />
          </div>
        ) : (
          alternativeBody
        )}
        <AlternativeOption
          onClick={markAnswer}
          disabled={!isEditable || corrected}
          selected={markedAnswers[questionIndex] === id}
          title={i18n.__('quiz_mark_correct_answer')}
          className={className}
        />
      </>
    );
  };

  const getViewForQuestion = (question, questionIndex) => {
    const correctAlternative = question.QuizAlternatives.find((alternative) => alternative.correct);
    const isQuestionCorrect = markedAnswers[questionIndex] === correctAlternative?.id;

    const editorPanel = (
      <Alternatives>
        {question.QuizAlternatives.map((alternative) => (
          <AlternativeWrapper key={alternative.id}>
            {getViewForAlternative(alternative, questionIndex, isQuestionCorrect)}
          </AlternativeWrapper>
        ))}
      </Alternatives>
    );

    const headerRightPanel = <ImmersiveReaderButton />;

    return (
      <NodeActivityEditor
        type={type}
        index={questionIndex}
        legacyContent={question}
        headerText={formatBody(question.body)}
        headerRightPanel={headerRightPanel}
        editorPanel={editorPanel}
        className={corrected && !isQuestionCorrect ? 'error' : ''}
      />
    );
  };

  const mappedQuestions = questions.map((question, index) => (
    <Question id={`question_${index}`} key={question.id} className={'ir-content'}>
      {getViewForQuestion(question, index)}
    </Question>
  ));

  const correctQuiz = (oldAnswers) => {
    let correct = 0;
    questions.forEach((question, index) => {
      const correctAnswer = question.QuizAlternatives.find((alternative) => alternative.correct);
      if (correctAnswer?.id === (oldAnswers || markedAnswers)[index]) {
        correct++;
      }
    });
    setCorrectAnswers(correct);
    setCorrected(true);
    if (!oldAnswers) {
      setDoSave(true);
    }
  };

  const resetQuiz = () => {
    if (redoable && !locked) {
      setMarkedAnswers(new Array(questionCount));
      setCorrectAnswers(0);
      setCorrected(false);
      saveReport({ ...report, state: 'active', type });
    }
  };

  const ActionButton = corrected ? PrimaryButton : IconButton;

  return (
    <>
      <ViewSection>
        <Questions>{mappedQuestions}</Questions>
        <QuizInfo>{i18n.__(redoable ? 'correlation_redoable_desc' : 'correlation_not_redoable_desc')}</QuizInfo>
      </ViewSection>
      <SubmitPanel className={'ir-hide'}>
        {progress ? (
          <ProgressPanel>
            {locked ? (
              <LockInfo>{i18n.__('editor_locked_desc')}</LockInfo>
            ) : redoable || !corrected ? (
              <ActionButton
                icon={CheckIcon}
                disabled={progress !== questionCount || locked || (!redoable && corrected)}
                onClick={corrected ? resetQuiz : () => correctQuiz()}
                text={i18n.__(corrected ? 'app_reset' : 'app_correct')}
              >
                {i18n.__(corrected ? 'app_reset' : 'app_correct')}
              </ActionButton>
            ) : (
              <div> </div>
            )}
            <Info>
              {corrected
                ? i18n.__('quiz_correcting_result', correctAnswers, questionCount)
                : i18n.__('quiz_ongoing_question_count', progress, questionCount)}
              <ProgressBar
                key={progress}
                progress={Math.round(((corrected ? correctAnswers : progress) / questionCount) * 100)}
              />
            </Info>
          </ProgressPanel>
        ) : (
          <InfoPanel>
            <Info>{i18n.__('quiz_question_count', questionCount)}</Info>
          </InfoPanel>
        )}
      </SubmitPanel>
    </>
  );
};

export default QuizContentViewer;

QuizContentViewer.propTypes = {
  type: PropTypes.string.isRequired,
  report: PropTypes.object,
  saveReport: PropTypes.func.isRequired,
  data: PropTypes.object,
  isTeacher: PropTypes.bool.isRequired,
};
